import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Alert } from '@/components/Alert'
import { getApiClient, unwrap, ApiProblemError, type Problem } from '@/data/apiClient'
import { triggerBothBackups } from '@/data/triggerBackups'
import { OB_WZ_6_COPY } from '@/copy/onboarding'
import { FormError, RateLimitedNotice, UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import type { components } from '@/api/generated/schema'

type JobRef = components['schemas']['JobRef']

/** `ob-wz-6` — `workspaceCompleteOnboarding` (§12.6.3). "Deferring completes onboarding too" —
 * one call, an explicit `start_now` choice, never a separate "skip" state.
 *
 * **`start_now` does not actually launch anything server-side (found live, 2026-09-25) —
 * `workspaceCompleteOnboarding`'s own response always carries `started: null`.**
 * `services/api/handlers/workspace/complete_onboarding.py`'s own docstring says so plainly:
 * `launch.py`'s DB-backed collaborators (job row writer, concurrency slot, control-plane client)
 * are the Job tag's to build and are not wired yet. Rather than wait on that backend work, "Start
 * my first backup" now fires the exact same two `jobTriggerBackup` calls (`DATA_BACKUP` +
 * `CONFIG_BACKUP`) the on-demand backup page uses, via the shared `triggerBothBackups` — these are
 * the same two jobs onboarding was always going to run on schedule anyway, just started
 * immediately instead of waiting for tonight's window. `onCompleted` is handed a real `JobRef` when
 * this ran, so the caller can route to the jobs list rather than the dashboard.
 *
 * **`pane`/`wizard-prose`/`note` added W166** — each on content this screen already rendered
 * (the root, the subheading, the "why" panel's own body text); no new copy.
 *
 * **`configBackupNote`** — both jobs are triggered together now (see above), but they remain two
 * fully independent, separately-scheduled passes (`BackupKind`: "deliberately decoupled and hours
 * apart") that do not wait on each other — the note's own point (a new customer should know the
 * data pass and the configuration pass are different things on different clocks) still holds. */
export function ObWz6({
  orgId,
  workspaceId,
  onCompleted,
}: {
  orgId: string
  workspaceId: string
  onCompleted: (started: JobRef | null) => void
}) {
  const complete = useMutation({
    mutationFn: async (startNow: boolean): Promise<JobRef | null> => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/onboarding/complete', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: { start_now: startNow },
      })
      unwrap(response as { data?: { workspace: unknown; started: JobRef | null }; error?: Problem })
      if (!startNow) return null
      const [started] = await triggerBothBackups(orgId, workspaceId)
      return started ?? null
    },
    onSuccess: (started) => onCompleted(started),
  })

  const problem = complete.error instanceof ApiProblemError ? complete.error.problem : undefined

  return (
    <div data-scr={unitId('ob-wz-6')} data-cmp="pane" className="p-10 max-w-[660px] mx-auto">
      <h3 className="m-0 mb-3 text-heading font-semibold text-center">{OB_WZ_6_COPY.heading}</h3>
      <p data-cmp="wizard-prose" className="mb-8 text-center text-tx2">{OB_WZ_6_COPY.subheading}</p>

      <div className="mb-7">
        <Alert variant="info">{OB_WZ_6_COPY.progressiveNote}</Alert>
      </div>

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-8">
        <div data-cmp="panel-header" className="p-6 border-b border-bd">
          <h3 className="m-0 text-body-lg font-semibold">{OB_WZ_6_COPY.whyTitle}</h3>
        </div>
        <p data-cmp="note" className="p-6 text-small leading-relaxed text-tx2">{OB_WZ_6_COPY.whyNote}</p>
        <p data-cmp="note" className="px-6 pb-6 text-small leading-relaxed text-tx2">{OB_WZ_6_COPY.configBackupNote}</p>
      </div>

      {problem && <ObWz6Refusal problem={problem} />}

      <div className="flex gap-5 justify-center items-center">
        <Button data-act={actionId('workspace.completeOnboarding')} disabled={complete.isPending} onClick={() => complete.mutate(true)}>
          {complete.isPending ? 'Starting…' : OB_WZ_6_COPY.startButton}
        </Button>
        <a
          href="#"
          data-act={actionId('workspace.completeOnboarding')}
          onClick={(e) => { e.preventDefault(); complete.mutate(false) }}
          className="text-link text-small"
        >
          {OB_WZ_6_COPY.skipLink}
        </a>
      </div>
    </div>
  )
}

function ObWz6Refusal({ problem }: { problem: Problem }) {
  if (problem.refusal_kind === 'RATE_LIMITED') return <RateLimitedNotice problem={problem} />
  return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
}
